import { useState, useEffect } from 'react'
import styles from './WorkspaceSettings.module.scss'
import {
    ScoopSpinner,
    ScoopText,
    ScoopButton,
    ScoopSelector,
    ScoopTable,
    ScoopInfoTooltip,
    ScoopSkeleton,
    ScoopTab,
    useToast,
} from '../../../ui'
import { EVENTS, trackEvent } from '../../../../events'

const PERMISSION_OPTIONS = [
    { label: 'Owner', value: 'Owner' },
    { label: 'Admin', value: 'Admin' },
    { label: 'Member', value: 'Member' },
]

const DATE_PROPERTY_OPTIONS = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday',
]

const workspaceTabs = [
    { key: 'members', label: 'Members' },
    { key: 'configuration', label: 'Configuration' },
]

export const WorkspaceSettings = ({
    getRole,
    accountMembers,
    loadingAccountMembers,
    addUser,
    isAddingUser,
    columns,
    members,
    loadingMembers,
    workspaceData,
    updateWeekStartDay,
    isUpdatingWeekStartDay,
    workspaces,
    selectedWorkspace,
    handleChangeWorkspace,
    BUTTONS,
}) => {
    const toast = useToast()
    // Internal state
    const [selectedWorkspaceTab, setSelectedWorkspaceTab] = useState('members')
    const [selectedUser, setSelectedUser] = useState('')
    const [selectedPermission, setSelectedPermission] = useState(PERMISSION_OPTIONS[2].value) // Default to 'Member'
    const [selectedDateProperty, setSelectedDateProperty] = useState(DATE_PROPERTY_OPTIONS[0])

    // Sync date property with workspace data
    useEffect(() => {
        if (workspaceData?.weekStartDay !== undefined) {
            const dayIndex = workspaceData.weekStartDay === 0 ? 6 : workspaceData.weekStartDay - 1
            setSelectedDateProperty(DATE_PROPERTY_OPTIONS[dayIndex])
        }
    }, [workspaceData?.weekStartDay])

    const handleAddUserToWorkspace = () => {
        if (!selectedUser) {
            return
        }

        addUser({
            workspaceID: selectedWorkspace,
            userID: selectedUser,
            role: selectedPermission,
            onSuccess: () => {
                trackEvent(EVENTS.ACCOUNT.ADD_MEMBER_TO_WORKSPACE, { role: selectedPermission })
                setSelectedUser('')
                setSelectedPermission(PERMISSION_OPTIONS[2].value) // Reset to 'Member'
                toast.success('User added to workspace successfully')
            },
            onError: (error) => {
                toast.error(error?.message || 'Failed to add user to workspace')
            },
        })
    }

    // Format account members for dropdown and filter out users already in workspace
    const availableUsers = (accountMembers || [])
        .map((user) => ({
            label: user.userName,
            value: user.userID,
        }))
        .filter((colleague) => !members.find((member) => member.userID === colleague.value))

    const handleUpdateDateProperty = () => {
        updateWeekStartDay({
            weekStartDay: DATE_PROPERTY_OPTIONS.indexOf(selectedDateProperty) + 1,
            onSuccess: () => {
                // Success handled by parent
            },
        })
    }

    const renderWorkspaceTabContent = () => {
        switch (selectedWorkspaceTab) {
            case 'members':
                return (
                    <>
                        {['owner', 'admin'].includes(getRole()?.toLowerCase()) && (
                            <div className={styles.addMemberSection}>
                                <div className={styles.inviteRow}>
                                    <div className={styles.userSelectField}>
                                        <ScoopSelector
                                            label="Add team member to workspace"
                                            value={selectedUser}
                                            onChange={(e) => setSelectedUser(e.target.value)}
                                            options={
                                                availableUsers.length > 0
                                                    ? availableUsers
                                                    : [{ label: 'No users available', value: '' }]
                                            }
                                            size="sm"
                                            maxHeight={200}
                                        />
                                    </div>
                                    <div className={styles.permissionField}>
                                        <ScoopSelector
                                            label="Role"
                                            value={selectedPermission}
                                            onChange={(e) => setSelectedPermission(e.target.value)}
                                            options={PERMISSION_OPTIONS}
                                            size="sm"
                                        />
                                    </div>
                                    <ScoopButton
                                        disabled={
                                            !selectedUser ||
                                            isAddingUser ||
                                            availableUsers.length === 0
                                        }
                                        onClick={handleAddUserToWorkspace}
                                        variant="contained"
                                        className={styles.addButton}
                                    >
                                        {isAddingUser ? <ScoopSpinner /> : 'Add'}
                                    </ScoopButton>
                                </div>
                            </div>
                        )}
                        <div className={styles.tableContainer}>
                            <ScoopTable
                                columns={columns.map((column) => ({
                                    field: column.key,
                                    header: column.label,
                                    flex: 1,
                                    render: ({ row, rowIndex }) => column.render(row, rowIndex),
                                }))}
                                rows={members}
                                loading={loadingMembers}
                                emptyMessage="No members found"
                            />
                        </div>
                    </>
                )
            case 'configuration':
                return (
                    <div className={styles.configSection}>
                        <ScoopText className={styles.configTitle}>Date Properties</ScoopText>
                        <ScoopText className={styles.configDescription}>
                            Below you will find settings to adjust how dates are aggregated within
                            Scoop.
                        </ScoopText>
                        <div
                            className={`${styles.configControls} ${
                                !['Admin', 'Owner'].includes(getRole()) ? styles.disabled : ''
                            }`}
                        >
                            <ScoopSelector
                                label={
                                    <div className={styles.selectorLabel}>
                                        <span>Calendar Week Start</span>
                                        <ScoopInfoTooltip
                                            placement={'top'}
                                            title={
                                                'Week start specifies how you choose to aggregate at the weekly level (e.g Saturday means Saturday to Friday)'
                                            }
                                        />
                                    </div>
                                }
                                value={selectedDateProperty}
                                onChange={(e) => setSelectedDateProperty(e.target.value)}
                                options={DATE_PROPERTY_OPTIONS.map((day) => ({
                                    label: day,
                                    value: day,
                                }))}
                                size="sm"
                            />
                            {['Admin', 'Owner'].includes(getRole()) && (
                                <ScoopButton
                                    onClick={handleUpdateDateProperty}
                                    variant="contained"
                                    disabled={isUpdatingWeekStartDay}
                                    className={styles.updateButton}
                                >
                                    {isUpdatingWeekStartDay ? <ScoopSpinner /> : 'Update'}
                                </ScoopButton>
                            )}
                        </div>
                    </div>
                )
            default:
                return null
        }
    }

    return (
        <div className={styles.container}>
            {/* Left side: Workspace List */}
            <div className={styles.workspaceListSidebar}>
                <div className={styles.workspaceList}>
                    {workspaces.length === 0
                        ? [...Array(10)].map((_, index) => (
                              <ScoopSkeleton
                                  key={index}
                                  variant="rounded"
                                  width={220}
                                  height={40}
                              />
                          ))
                        : workspaces.map((workspace) => (
                              <div
                                  key={workspace.workspaceID}
                                  onClick={() => handleChangeWorkspace(workspace.workspaceID)}
                                  className={`${styles.workspaceItem} ${
                                      selectedWorkspace === workspace.workspaceID
                                          ? styles.selected
                                          : ''
                                  }`}
                              >
                                  <ScoopText className={styles.workspaceName}>
                                      {workspace.name}
                                  </ScoopText>
                              </div>
                          ))}
                </div>
            </div>

            {/* Right side: Content Area */}
            <div className={styles.contentArea}>
                {/* Top row: Workspace name/actions and Sub-tabs */}
                <div className={styles.topBar}>
                    {/* Sub-tabs */}
                    <div className={styles.subTabsContainer}>
                        <ScoopTab
                            noPadding
                            tabs={workspaceTabs}
                            value={selectedWorkspaceTab}
                            onChange={(e, newValue) => setSelectedWorkspaceTab(newValue)}
                        />
                    </div>
                    {/* Workspace actions */}
                    {getRole() === 'Owner' && (
                        <div className={styles.workspaceActions}>
                            {BUTTONS.map((button, index) => (
                                <ScoopButton
                                    onClick={button.onClick}
                                    key={index}
                                    variant="outlined"
                                    className={styles.actionButton}
                                >
                                    {button.icon}
                                    {button.label}
                                </ScoopButton>
                            ))}
                        </div>
                    )}
                </div>
                {/* Sub-tab Content */}
                {renderWorkspaceTabContent()}
            </div>
        </div>
    )
}
